import { useGetAnalysersConfigQuery, useGetRunsQuery, useGetTaskLogsQuery } from "../../../../store/api/kinesense";
import { Accordion, Button, Modal, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRunning } from "@fortawesome/free-solid-svg-icons";
import { ReviewVideoListItemTrayContentProps } from "./ReviewVideoListItemFooter";
import useInterval from "../../../../hooks/useInterval";
import { capitaliseFirst } from "../../../../utilities/helpers";
import { DateFormats } from "../../../../utilities/dates";
import dayjs from "dayjs";
import { useState } from "react";
import { ApplicationState } from "../../../../store";
import { useSelector } from "react-redux";

export type ReviewVideoListItemRunsProps = ReviewVideoListItemTrayContentProps & { forceUpdate: () => void };

export default function ReviewVideoListItemRuns(props: ReviewVideoListItemRunsProps) {
    const { user } = useSelector((state: ApplicationState) => state);
    const [openTaskLogTaskId, setOpenTaskLogTaskId] = useState<string | null>(null);

    const mediaId = props.data.mediaId;
    const { data, isSuccess, isFetching } = useGetRunsQuery({ mediaId }, { skip: !mediaId });
    const isLoadingRuns = isFetching || !isSuccess;
    const runs = data ?? [];

    const { data: analyserConfig, isLoading: isLoadingAnalysers } = useGetAnalysersConfigQuery();
    function getMatchingAnalyser(analyserId: string) {
        return analyserConfig?.analysers.find((a) => analyserId === a.analyserId);
    }

    // Additional functionality needed since height of the tray needs to be dynamically updated
    const { resumeInterval, pauseInterval } = useInterval(props.forceUpdate, 20, false);

    if (isLoadingRuns || isLoadingAnalysers) {
        return;
    }

    return (
        <div className="inner">
            {runs.length === 0 ? (
                <p className="text-center text-muted">No algorithms have been applied to this media source</p>
            ) : (
                <section className="overflow-hidden">
                    <Accordion flush>
                        {runs.map((r) => {
                            const analyser = getMatchingAnalyser(r.analyserId);
                            const taskLogs = useGetTaskLogsQuery(
                                { projectId: props.data.projectId, taskId: r.taskRef.taskId },
                                { skip: !r.taskRef?.taskId },
                            );
                            console.log(taskLogs.data, taskLogs.data?.data);

                            if (!analyser) {
                                return undefined;
                            }

                            // NOTE: when we support more algorithms, different icons need to be
                            // added here based on the `analyserId`
                            const icon = faRunning;

                            const lastUpdated = dayjs(r.lastUpdatedAt).format(DateFormats.dayMonthYearWithTimeSeconds);
                            const isLogModalOpen = openTaskLogTaskId === r.taskRef.taskId;
                            const hasTaskId = Boolean(r.taskRef?.taskId);

                            return (
                                <Accordion.Item eventKey={r.analyserId} key={r.analyserId}>
                                    <Accordion.Header>
                                        <div className="gap-2 d-flex">
                                            <FontAwesomeIcon icon={icon} />
                                            <span title={analyser.description}>{analyser.displayName}</span>
                                        </div>
                                    </Accordion.Header>
                                    <Accordion.Body
                                        onExiting={resumeInterval}
                                        onExited={pauseInterval}
                                        onEntering={resumeInterval}
                                        onEntered={pauseInterval}
                                    >
                                        <>
                                            <Table borderless className="details-table">
                                                <tbody>
                                                    <tr>
                                                        <td>Status</td>
                                                        <td className="w-100">
                                                            {capitaliseFirst(r.status.currentStage)}
                                                        </td>
                                                    </tr>

                                                    <tr>
                                                        <td>Last Updated</td>
                                                        <td>{lastUpdated}</td>
                                                    </tr>
                                                </tbody>
                                            </Table>

                                            {user.permissions.isAdmin && (
                                                <section style={{ marginTop: "1rem" }}>
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <Button
                                                            size="sm"
                                                            variant="secondary"
                                                            onClick={() => setOpenTaskLogTaskId(r.taskRef.taskId)}
                                                            disabled={!hasTaskId}
                                                        >
                                                            View import log
                                                        </Button>
                                                    </div>

                                                    <Modal
                                                        show={isLogModalOpen}
                                                        onHide={() => setOpenTaskLogTaskId(null)}
                                                        size="xl"
                                                        centered
                                                    >
                                                        <Modal.Header closeButton>
                                                            <Modal.Title>Import Log</Modal.Title>
                                                        </Modal.Header>
                                                        <Modal.Body>
                                                            {taskLogs.data && taskLogs.data.data.length > 0 ? (
                                                                <Table className="log-entries">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Time</th>
                                                                            <th>Type</th>
                                                                            <th>Job Type</th>
                                                                            <th>Job</th>
                                                                            <th>Extra Data</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {taskLogs.data.data
                                                                            .toSorted(
                                                                                (a, b) => a.timestamp - b.timestamp,
                                                                            )
                                                                            .map((logEntry, index) => (
                                                                                <tr className="log-entry" key={index}>
                                                                                    <td className="timestamp">
                                                                                        {dayjs(
                                                                                            logEntry.timestamp,
                                                                                        ).format(
                                                                                            DateFormats.timeSeconds,
                                                                                        )}
                                                                                    </td>
                                                                                    <td>{logEntry.type}</td>
                                                                                    <td>{logEntry.job?.type}</td>
                                                                                    <td>{logEntry.job?.jobId}</td>
                                                                                    <td>
                                                                                        {logEntry.extraData ? (
                                                                                            <pre
                                                                                                style={{
                                                                                                    margin: 0,
                                                                                                    fontSize: "0.85em",
                                                                                                }}
                                                                                            >
                                                                                                {JSON.stringify(
                                                                                                    logEntry.extraData,
                                                                                                    null,
                                                                                                    2,
                                                                                                )}
                                                                                            </pre>
                                                                                        ) : (
                                                                                            "-"
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                    </tbody>
                                                                </Table>
                                                            ) : (
                                                                <p className="text-muted mb-0">
                                                                    No log entries available
                                                                </p>
                                                            )}
                                                        </Modal.Body>
                                                    </Modal>
                                                </section>
                                            )}
                                        </>
                                    </Accordion.Body>
                                </Accordion.Item>
                            );
                        })}
                    </Accordion>
                </section>
            )}
        </div>
    );
}
